// One stroke-drawn set, sized in ems so an icon follows whatever text it sits
// beside. Text glyphs like ⟳ and ⌕ were doing this job before; they render
// differently on every platform and none of them line up with a baseline.

const PATHS = {
  home: <><path d="m3 10 9-7 9 7v10H3Z" /><path d="M9 20v-7h6v7" /></>,
  menu: <path d="M4 6h16M4 12h16M4 18h16" />,
  sidebar: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 3v18" />
    </>
  ),
  book: (
    <>
      <path d="M12 6.5C9.5 4.5 6 4 3 5v14c3-1 6.5-.5 9 1.5 2.5-2 6-2.5 9-1.5V5c-3-1-6.5-.5-9 1.5Z" />
      <path d="M12 6.5v14" />
    </>
  ),
  leaf: (
    <>
      <path d="M19.5 4.5C8 3 3 9 5.5 15.5S21 20 19.5 4.5Z" />
      <path d="M4 21 15.5 9.5" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5 12 5s9.5 7 9.5 7S18 19 12 19s-9.5-7-9.5-7Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4.5 4.5" />
    </>
  ),
  refresh: (
    <>
      <path d="M20.5 12a8.5 8.5 0 1 1-2.5-6" />
      <path d="M20.5 3.5v5h-5" />
    </>
  ),
  check: <path d="M4.5 12.5 L9.5 17.5 L19.5 6.5" />,
  user: (
    <>
      <circle cx="12" cy="8" r="3.75" />
      <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" />
    </>
  ),
  users: (
    <>
      <circle cx="9.5" cy="8.5" r="3.25" />
      <path d="M3 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16.5 5.6a3.25 3.25 0 0 1 0 5.8" />
      <path d="M18 14.4a6.5 6.5 0 0 1 3 5.6" />
    </>
  ),
  sliders: (
    <>
      <path d="M4 8h10M18 8h2M4 16h4M12 16h8" />
      <circle cx="16" cy="8" r="2" />
      <circle cx="10" cy="16" r="2" />
    </>
  ),
  signOut: (
    <>
      <path d="M15 8V5.5a1.5 1.5 0 0 0-1.5-1.5h-7A1.5 1.5 0 0 0 5 5.5v13A1.5 1.5 0 0 0 6.5 20h7a1.5 1.5 0 0 0 1.5-1.5V16" />
      <path d="M10.5 12h10M17.5 8.5l3.5 3.5-3.5 3.5" />
    </>
  ),
  alert: (
    <>
      <path d="M12 4.5 21 19.5H3z" />
      <path d="M12 10.5v3.5" />
      <path d="M12 17h.01" />
    </>
  ),
  close: <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />,
  arrowRight: <path d="M4.5 12h14M13 6.5l5.5 5.5-5.5 5.5" />,
  chevronDown: <path d="M6.5 9.5 12 15l5.5-5.5" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5.2l3.2 1.9" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5.5" />
      <path d="M12 7.8h.01" />
    </>
  ),
  trendUp: (
    <>
      <path d="M3.5 16.5 9 11l4 4 7.5-7.5" />
      <path d="M15.5 7.5h5v5" />
    </>
  ),
  trendDown: (
    <>
      <path d="M3.5 7.5 9 13l4-4 7.5 7.5" />
      <path d="M15.5 16.5h5v-5" />
    </>
  ),
  trendFlat: <path d="M4 12h16" />,
  // Risk-adjusted ranking: a dial reading part way round.
  gauge: (
    <>
      <path d="M4 17.5a8.5 8.5 0 1 1 16 0" />
      <path d="M12 17.5 16 11" />
    </>
  ),
  chart: (
    <>
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <path d="M8 16v-4M12.5 16V8M17 16v-6" />
    </>
  ),
  caretLeft: <path d="M14 6.5 8.5 12l5.5 5.5" />,
  chevronRight: <path d="m9 6 6 6-6 6" />
};

export default function Icon({ name, size = '1em', className = '', strokeWidth = 2 }) {
  const d = PATHS[name];
  if (!d) return null;

  return (
    <svg
      className={`icon ${className}`.trim()}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {d}
    </svg>
  );
}

export const ICON_NAMES = Object.keys(PATHS);
